import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import { TurfTalk } from "@/api/client";
import { OWNER_EMAIL, MIN_PASSWORD, validateOwnerClaim, ownerClaimMessage } from "@/lib/ownerClaim";

export default function OwnerClaim() {
  const navigate = useNavigate();
  const [secret, setSecret] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  // Never persist credentials: wipe both fields on unmount as well.
  useEffect(() => () => { setSecret(""); setPassword(""); }, []);

  const wipe = () => { setSecret(""); setPassword(""); };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const invalid = validateOwnerClaim(secret, password);
    if (invalid) { setErr(invalid); return; }
    setBusy(true);
    try {
      await TurfTalk.ownerClaim(secret, password);
      wipe();
      setDone(true);
    } catch (e2) {
      setErr(ownerClaimMessage(e2));
      wipe(); // clear the secret/password even on failure
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B1220] p-6" data-testid="owner-claim-page">
      <div className="w-full max-w-md tt-rise">
        <div className="flex items-center gap-3 mb-8">
          <img src="/tt-mic-cropped.png" alt="Turf Talk" className="h-14 w-auto object-contain" />
          <div className="flex flex-col">
            <span className="font-display text-2xl leading-none text-white">TURF TALK</span>
            <span className="font-semibold text-xs tracking-[0.02em] text-white/85 mt-1">by SportChant</span>
          </div>
        </div>

        {done ? (
          <div data-testid="owner-claim-success">
            <div className="flex items-center gap-2 text-[#4ea152] mb-3"><CheckCircle2 size={22} /><span className="font-display text-2xl">Owner access created</span></div>
            <p className="text-white/60 text-sm mb-6">
              The owner account <span className="text-white">{OWNER_EMAIL}</span> has been set up. For security you're not signed in automatically — continue to sign in with your new password.
            </p>
            <Link to="/login" data-testid="owner-claim-to-login" className="inline-flex items-center justify-center w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium transition-colors">
              Go to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="text-[11px] tracking-[0.2em] text-[#4ea152] font-display mb-1 flex items-center gap-1.5"><ShieldCheck size={13} /> OWNER SETUP</div>
            <h1 className="font-display text-4xl text-white mb-2">Claim owner access</h1>
            <p className="text-white/55 text-sm mb-6" data-testid="owner-claim-email">
              This creates the owner account <span className="text-white">{OWNER_EMAIL}</span>. This is a one-time setup.
            </p>

            <form onSubmit={submit} data-testid="owner-claim-form" autoComplete="off">
              <label className="block mb-4">
                <span className="block text-sm text-white/70 mb-1.5">One-time owner secret</span>
                <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="owner-claim-secret"
                  autoComplete="off" spellCheck={false}
                  className="w-full h-11 bg-[#131a24] border border-[#2A3544] rounded-lg px-3 text-white placeholder:text-white/30 focus:border-[#2F7D32] outline-none transition-colors" />
              </label>
              <label className="block mb-2">
                <span className="block text-sm text-white/70 mb-1.5">Choose password</span>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="owner-claim-password"
                  autoComplete="new-password" spellCheck={false} placeholder="At least 12 characters"
                  className="w-full h-11 bg-[#131a24] border border-[#2A3544] rounded-lg px-3 text-white placeholder:text-white/30 focus:border-[#2F7D32] outline-none transition-colors" />
              </label>
              <p className="text-[11px] text-white/35 mb-4">Minimum {MIN_PASSWORD} characters.</p>

              {err && <div className="text-[#ff6b60] text-sm mb-4" data-testid="owner-claim-error">{err}</div>}

              <button disabled={busy} data-testid="owner-claim-submit" className="w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium transition-colors disabled:opacity-50">
                {busy ? "Creating…" : "Create owner access"}
              </button>
            </form>
            <p className="text-white/50 text-sm mt-5">Already set up? <Link to="/login" className="text-[#4ea152] hover:underline" data-testid="owner-claim-login-link">Sign in</Link></p>
          </>
        )}
      </div>
    </div>
  );
}
